import {getLogger} from '@/shared/utils'

/**
 * Client-side reader for the published viewer manifest — the map from a viewer's app name to the
 * version currently deployed on the CDN.
 *
 * The manifest is a plain, public, static JSON published by `omicsbox-web-components/deploy.sh` to
 * the same CDN path as the viewer bundles themselves (`schema-v1/<env>/manifest.json`, sibling of
 * `viewers/`). Reading it here makes the frontend able to resolve a viewer URL on its own, which is
 * what lets a plain file (png/pdf/txt) open with no OBserver round trip at all — see
 * `open-in-viewer.ts`. The OBserver reads the very same file (`ViewerManifestProvider`), so the two
 * agree by construction.
 *
 * Deliberately a module-level cache rather than a react-query hook: the main consumer,
 * {@link openFileInViewer}, is a plain module function, and `file-table-actions.ts` invokes it from
 * an object literal with no React context to hang a hook off. The `QueryClient` in `index.tsx` is
 * module-local and unexported, so `fetchQuery` is not reachable either.
 */

/** One compatibility window of a viewer, as published in the manifest. */
interface ViewerManifestEntry {
  version: string
  md5?: string
  /** Inclusive lower bound on the OmicsBox *desktop* app version. */
  minAppVersion?: string
  /** Exclusive upper bound; absent means "this is the current window". */
  maxAppVersion?: string
}

export interface ViewerManifest {
  viewers: Record<string, ViewerManifestEntry[]>
}

/**
 * Read lazily rather than captured at module load so tests can point it somewhere. CRA's DefinePlugin
 * substitutes the literal wherever the member expression appears, so this still inlines in a build.
 */
function manifestUrl(): string | undefined {
  return process.env.REACT_APP_VIEWERS_MANIFEST_URL
}

/**
 * Lowest viewer version that implements a capability the frontend depends on. A deployed viewer
 * older than this cannot serve what we would ask of it, so it is reported as unresolvable (an
 * explained error tab) rather than opened into a spinner that never resolves.
 *
 * `html-viewer` 1.0.2 is the first build that understands a storage-backed `getApiUrl` payload
 * (presigned URL + format) instead of an OBserver controller endpoint. Older builds hand the
 * presigned URL to their ConnectionManager and hang.
 */
const MINIMUM_VERSIONS: Record<string, string> = {
  'html-viewer': '1.0.2',
}

/**
 * How long a fetched manifest is trusted before the next read goes back to the CDN.
 *
 * Bounds how long a browser session can keep resolving viewers against a superseded manifest: a
 * viewer published mid-session used to be invisible until the user reloaded the page, because the
 * copy read on the first paint was kept for the whole life of the JS session. Deliberately checked
 * lazily, at the point of use — no timer runs in the background just to age a cache out.
 */
const MANIFEST_TTL_MS = 60 * 60 * 1000

/** In-flight fetch, so concurrent callers share one request. Cleared as soon as it settles. */
let inFlight: Promise<ViewerManifest | undefined> | undefined
/** Last successfully parsed manifest, for the synchronous {@link peekViewerVersion}. */
let cached: ViewerManifest | undefined
/** `Date.now()` when {@link cached} was fetched. Only ever advanced by a *successful* fetch. */
let cachedAt = 0

/** True while {@link cached} holds a manifest that has not yet aged past {@link MANIFEST_TTL_MS}. */
function isFresh(): boolean {
  return cached !== undefined && Date.now() - cachedAt < MANIFEST_TTL_MS
}

/**
 * Whether {@link peekViewerVersion} would answer from a manifest that is still current. False both
 * before the first read and once the cached copy has aged out — the two cases where a synchronous
 * answer may name a version that is no longer the deployed one, or no version at all.
 */
export function isViewerManifestFresh(): boolean {
  return isFresh()
}

/** Compare dotted numeric versions; non-numeric segments sort as 0. Returns <0, 0 or >0. */
function compareVersions(a: string, b: string): number {
  const left = a.split('.')
  const right = b.split('.')
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const diff = (parseInt(left[i], 10) || 0) - (parseInt(right[i], 10) || 0)
    if (diff !== 0) {
      return diff
    }
  }
  return 0
}

/**
 * Read the manifest, re-fetching it when the cached copy has aged past {@link MANIFEST_TTL_MS}.
 * Resolves to `undefined` rather than throwing when the manifest is unavailable or malformed, so
 * callers degrade to whatever version they already hold instead of breaking. Nothing is memoised on
 * failure, so a transient CDN blip does not poison the rest of the session — this is why
 * react-query's `retry`/`cacheTime` defaults would be wrong here: they would cache the *error* for
 * 24h.
 */
export function loadViewerManifest(): Promise<ViewerManifest | undefined> {
  if (isFresh()) {
    return Promise.resolve(cached)
  }
  return refreshManifest()
}

/**
 * Single-flight re-fetch: while one request is in the air every caller shares it, so a burst of
 * viewers mounting together still costs one round trip.
 */
function refreshManifest(): Promise<ViewerManifest | undefined> {
  if (!inFlight) {
    inFlight = fetchManifest()
      .catch((error) => {
        getLogger().warn('Viewer manifest unavailable; falling back to server-resolved versions:', error)
        // Serve the last known good manifest instead of downgrading callers to `undefined`. Its age
        // is left untouched, so the next read tries again rather than caching this failure.
        return cached
      })
      .finally(() => {
        inFlight = undefined
      })
  }
  return inFlight
}

async function fetchManifest(): Promise<ViewerManifest | undefined> {
  const url = manifestUrl()
  if (!url) {
    throw new Error('REACT_APP_VIEWERS_MANIFEST_URL is not configured')
  }
  // no-cache revalidates rather than re-downloads: the manifest carries an ETag, so this is a 304 in
  // the common case. Worth the round trip — the manifest ships with no Cache-Control and its CDN path
  // is not invalidated on deploy, so a heuristically-cached copy can be weeks stale.
  const response = await fetch(url, {cache: 'no-cache'})
  if (!response.ok) {
    throw new Error(`Failed to fetch viewer manifest (${response.status})`)
  }
  const manifest = (await response.json()) as ViewerManifest
  if (!manifest || typeof manifest !== 'object' || !manifest.viewers) {
    // A 200 carrying the wrong JSON is a real failure mode, not a hypothetical: point the URL at the
    // app's own origin and CRA serves `public/manifest.json` (the PWA stub) with a 200.
    throw new Error('Viewer manifest has no "viewers" map')
  }
  cached = manifest
  cachedAt = Date.now()
  return manifest
}

/**
 * Pick the version of {@code app} a web client should load.
 *
 * The manifest's compatibility windows are keyed on the OmicsBox *desktop* app version, which a web
 * client does not have. The entry with no `maxAppVersion` is the publisher's own notion of "current"
 * (`deploy.sh` selects it the same way, and `break-compat.js` only ever caps superseded windows), so
 * that is what we take. If every window is capped the viewer has been retired for this generation —
 * answer `undefined` rather than loading a version explicitly declared incompatible.
 */
function pickVersion(manifest: ViewerManifest | undefined, app: string): string | undefined {
  const entries = manifest?.viewers?.[app]
  if (!Array.isArray(entries) || entries.length === 0) {
    return undefined
  }
  const current = entries.find((entry) => entry && !entry.maxAppVersion && entry.version)
  if (!current) {
    return undefined
  }
  const floor = MINIMUM_VERSIONS[app]
  if (floor && compareVersions(current.version, floor) < 0) {
    getLogger().warn(
      `Deployed ${app} is ${current.version}, below the ${floor} this app requires; treating it as unavailable.`
    )
    return undefined
  }
  return current.version
}

/** Resolve the deployed version of a viewer, fetching the manifest if it has not been read yet. */
export async function resolveViewerVersion(app: string): Promise<string | undefined> {
  return pickVersion(await loadViewerManifest(), app)
}

/**
 * Synchronous read of an already-fetched manifest, for render paths that cannot await. Returns
 * `undefined` until {@link loadViewerManifest} has resolved, so callers must keep a fallback.
 *
 * An aged copy still answers with its last known good version — a viewer version that is merely old
 * is far better than none, which callers turn into an error tab — but noticing the age here starts
 * the re-fetch, so whoever peeks next (the next tab the user opens) gets the newer answer.
 */
export function peekViewerVersion(app: string): string | undefined {
  if (cached !== undefined && !isFresh()) {
    void refreshManifest()
  }
  return pickVersion(cached, app)
}

/** Test seam: drop the cached manifest so each test starts from a clean fetch. */
export function resetViewerManifestCache(): void {
  inFlight = undefined
  cached = undefined
  cachedAt = 0
}
